import { useApolloClient, NetworkStatus } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { isNil } from "lodash-es";
import React from "react";
import { useLatest } from "react-use";
import type { SearchItemsFilters } from "../../../../clients/graphqlTypes.js";
import { useStateWithUrlParam } from "../../../commons/hooks/useStateWithUrlParam.js";
import { useStateWithUrlParams } from "../../../commons/hooks/useStateWithUrlParams.js";
import { useSearchItemsQuery } from "./useItemsSearch.generated.js";
import { useDebouncedValue } from "@msys/ui";

export type ItemsSearchFilters = SearchItemsFilters;

export const useItemsSearch = <Item extends { id: string }>({
  projectId,
  docId,
  filtersUrlParam = "filters",
  searchUrlParam = "search",
  onSearchApplied,
}: {
  projectId: string | null;
  docId: string;
  filtersUrlParam?: string;
  searchUrlParam?: string;
  onSearchApplied?: (v: {
    foundItemIds: string[];
    parentItemIds: string[];
  }) => void;
}) => {
  const [filters, setFilters] = useStateWithUrlParams<ItemsSearchFilters>(
    filtersUrlParam,
    {}
  );
  const [search, setSearch] = useStateWithUrlParam<string>(searchUrlParam, "");

  const filtersDebounced = useDebouncedValue(filters, 500, {
    leading: false,
    trailing: true,
  });

  const isSearchEnabled = search.trim() !== "";

  const isSearchOrFiltersEnabled =
    search.trim() !== "" ||
    (filtersDebounced.type && filtersDebounced.type.length > 0) ||
    (filtersDebounced.binding && filtersDebounced.binding.length > 0) ||
    !isNil(filtersDebounced.needsAgreement);

  const client = useApolloClient();
  const query = useSearchItemsQuery({
    client,
    variables: {
      docId,
      projectId,
      filters: filtersDebounced,
      search: search,
    },
    skip: !isSearchOrFiltersEnabled,
  });

  const latest = useLatest({ onSearchApplied });

  React.useEffect(() => {
    // This effect runs every time the query completes fetching or refetching
    const isDone =
      !query.loading && query.networkStatus === NetworkStatus.ready;

    if (isDone) {
      // Add your logic here that runs when fetch/refetch completes
      const isSearchEnabled = Boolean(
        query.variables?.search && query.variables.search.trim() !== ""
      );
      const foundItemIds =
        getDataOrNull(query.data?.searchItems)?.foundItemIds ?? [];
      const parentItemIds =
        getDataOrNull(query.data?.searchItems)?.parentItemIds ?? [];
      if (isSearchEnabled) {
        latest.current.onSearchApplied?.({ foundItemIds, parentItemIds });
      }
    }
  }, [
    query.loading,
    query.networkStatus,
    query.data,
    query.variables?.search,
    latest,
  ]);

  const resetFilters = React.useCallback(() => {
    setFilters({});
  }, [setFilters]);

  const foundItemIds = React.useMemo(
    () =>
      isSearchOrFiltersEnabled
        ? (getDataOrNull((query.data ?? query.previousData)?.searchItems)
            ?.foundItemIds ?? [])
        : [],
    [isSearchOrFiltersEnabled, query.data, query.previousData]
  );
  const parentItemIds = React.useMemo(
    () =>
      isSearchOrFiltersEnabled
        ? (getDataOrNull((query.data ?? query.previousData)?.searchItems)
            ?.parentItemIds ?? [])
        : [],
    [isSearchOrFiltersEnabled, query.data, query.previousData]
  );

  const filterItemSearch = React.useCallback(
    (item: Item) => {
      if (!isSearchOrFiltersEnabled || (!query.data && !query.previousData))
        return true;
      return parentItemIds.includes(item.id) || foundItemIds.includes(item.id);
    },
    [
      foundItemIds,
      isSearchOrFiltersEnabled,
      parentItemIds,
      query.data,
      query.previousData,
    ]
  );

  const [searchVisible, setSearchVisible] = React.useState<boolean>(
    search.trim() !== ""
  );

  const searchRef = React.useRef<HTMLInputElement>(null);

  const toggleSearchVisible = React.useCallback(() => {
    if (!searchVisible) {
      setSearchVisible(true);
      setTimeout(() => {
        searchRef.current?.focus();
      });
    } else {
      setSearchVisible(false);
      setSearch("");
    }
  }, [searchVisible, setSearch]);

  const isItemMuted = React.useCallback(
    (item: Item) => {
      return (
        isSearchEnabled &&
        parentItemIds.includes(item.id) &&
        !foundItemIds.includes(item.id)
      );
    },
    [foundItemIds, isSearchEnabled, parentItemIds]
  );

  return {
    filters,
    setFilters,
    resetFilters,
    search,
    setSearch,
    isSearchEnabled,
    isSearchOrFiltersEnabled,
    foundItemIds,
    parentItemIds,
    filterItemSearch,
    searchVisible,
    searchRef,
    toggleSearchVisible,
    isItemMuted,
  };
};
